// src/components/MarketInsights/components/AvailabilityTimeline.tsx

import React, { useState, useCallback } from "react";

import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { TimelineDataPoint } from "../types";
import { formatCurrency, formatDate } from "../utils/formatters";

interface AvailabilityTimelineProps {
  data: TimelineDataPoint[] | undefined;
  isLoading: boolean;
  onBarClick: (date: string) => void;
  selectedDate: string | null;
  includeAvailableNow: boolean;
  onIncludeAvailableNowChange: (value: boolean) => void;
}

type ViewMode = "total" | "by_bedrooms";

const AvailabilityTimeline: React.FC<AvailabilityTimelineProps> = ({
  data,
  isLoading,
  onBarClick,
  selectedDate,
  includeAvailableNow,
  onIncludeAvailableNowChange,
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("total");

  const handleBarClick = useCallback(
    (data: any, _index: number) => {
      if (data && data.date) {
        onBarClick(data.date);
      }
    },
    [onBarClick]
  );

  const handleChartClick = useCallback(
    (event: any) => {
      if (event && event.activePayload && event.activePayload.length > 0) {
        const payload = event.activePayload[0].payload;
        if (payload && payload.date) {
          onBarClick(payload.date);
        }
      }
    },
    [onBarClick]
  );

  if (isLoading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Skeleton width="40%" height={32} />
          <Skeleton
            variant="rectangular"
            height={300}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </CardContent>
      </Card>
    );
  }

  // Transform data for chart - ensure date is preserved
  const chartData = (data || []).map((item) => ({
    ...item,
    date: item.date, // Explicitly include date
    dateLabel: formatDate(item.date === "now" ? null : item.date),
    isSelected: item.date === selectedDate,
  }));

  // Colors for bedroom breakdown
  const bedroomColors = {
    studio: theme.palette.info.light,
    "1br": theme.palette.info.main,
    "2br": theme.palette.primary.main,
    "3br+": theme.palette.secondary.main,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const dataPoint = payload[0]?.payload as TimelineDataPoint & {
      dateLabel: string;
    };

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: theme.shadows[4],
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {dataPoint.dateLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {dataPoint.count} units available
        </Typography>
        {dataPoint.avg_price && (
          <Typography variant="body2" color="text.secondary">
            Avg: {formatCurrency(dataPoint.avg_price)}
          </Typography>
        )}
        {viewMode === "by_bedrooms" && dataPoint.by_bedrooms && (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Studio: {dataPoint.by_bedrooms.studio} | 1BR:{" "}
              {dataPoint.by_bedrooms["1br"]} | 2BR:{" "}
              {dataPoint.by_bedrooms["2br"]} | 3BR+:{" "}
              {dataPoint.by_bedrooms["3br+"]}
            </Typography>
          </Box>
        )}
        <Typography
          variant="caption"
          color="primary"
          sx={{ display: "block", mt: 1, fontWeight: 500 }}
        >
          Click to view units
        </Typography>
      </Box>
    );
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Upcoming Availability
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click a bar to view available units for that date
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAvailableNow}
                  onChange={(e) =>
                    onIncludeAvailableNowChange(e.target.checked)
                  }
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Include available now
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
            >
              <ToggleButton value="total">Total</ToggleButton>
              <ToggleButton value="by_bedrooms">By Beds</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {chartData.length === 0 ? (
          <Box
            sx={{
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              No upcoming availability data
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              {viewMode === "total" ? (
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                  onClick={handleChartClick}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={alpha(theme.palette.divider, 0.5)}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data, index) => handleBarClick(data, index)}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.isSelected
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.6)
                        }
                        stroke={
                          entry.isSelected ? theme.palette.primary.dark : "none"
                        }
                        strokeWidth={entry.isSelected ? 2 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                  onClick={handleChartClick}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={alpha(theme.palette.divider, 0.5)}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                  />
                  <Legend />
                  <Bar
                    dataKey="by_bedrooms.studio"
                    name="Studio"
                    stackId="a"
                    fill={bedroomColors.studio}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="by_bedrooms.1br"
                    name="1 BR"
                    stackId="a"
                    fill={bedroomColors["1br"]}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="by_bedrooms.2br"
                    name="2 BR"
                    stackId="a"
                    fill={bedroomColors["2br"]}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="by_bedrooms.3br+"
                    name="3+ BR"
                    stackId="a"
                    fill={bedroomColors["3br+"]}
                    cursor="pointer"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}

        {selectedDate && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2">
              Viewing units available on{" "}
              <strong>
                {formatDate(selectedDate === "now" ? null : selectedDate)}
              </strong>
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: "pointer", fontWeight: 500 }}
              onClick={() => onBarClick("")}
            >
              Clear selection
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilityTimeline;
